import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPricingSettingsRemote, listPrintProducts, setPricingSettingsRemote } from '../../../services/adminApi';
import { adminPath } from '../shell/adminNav';
import ChangeHistory from './ChangeHistory';
import PromoSection from './PromoSection';
import { Tag } from './ui';
import { centsToEuros, eurosToCents, formatEuros } from './productForm';
import './catalog.css';

// Tarifs et livraison : TOUT CE QUI N'EST PAS LE PRIX D'UN PRODUIT, plus une vue d'ensemble en LECTURE SEULE.
// Le prix d'un produit (coût, marge, prix de vente, port) se regle a UN seul endroit : la fiche du produit, dans « Produits et formats ».
// Ici : le prix du PDF et la remise du pack (valables pour tous les produits), le rappel des ports et des ecarts avec les devis.

function PrixGeneraux() {
  const { t } = useTranslation('admin');
  const [donnees, setDonnees] = useState(null);
  const [pdf, setPdf] = useState('');
  const [remise, setRemise] = useState('');
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [enCours, setEnCours] = useState(false);

  const appliquer = (d) => { setDonnees(d); setPdf(centsToEuros(d.pdfPriceCents)); setRemise(String(d.packDiscountPercent)); };

  useEffect(() => {
    getPricingSettingsRemote().then(appliquer).catch((err) => setErreur(err?.message || t('catalog.pricing.loadFailed')));
  }, [t]);

  const modifie = donnees && (eurosToCents(pdf) !== donnees.pdfPriceCents || Number(remise) !== donnees.packDiscountPercent);

  const enregistrer = async () => {
    setErreur('');
    setMessage('');
    const cents = eurosToCents(pdf);
    const pourcent = Number(remise);
    if (cents === null || Number.isNaN(cents) || !Number.isFinite(pourcent) || pourcent < 0 || pourcent > 100) { setErreur(t('catalog.pricing.invalid')); return; }
    setEnCours(true);
    try {
      // Seuls ces deux champs partent : les prix des formats sont conserves tels quels cote serveur.
      appliquer(await setPricingSettingsRemote({ pdfPriceCents: cents, packDiscountPercent: Math.round(pourcent) }));
      setMessage(t('catalog.pricing.saved'));
    } catch (err) {
      setErreur(err?.message || t('catalog.pricing.saveFailed'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <section className="adm-card">
      <h2>{t('catalog.pricing.generalTitle')}</h2>
      <p className="adm-note">{t('catalog.pricing.generalNote')}</p>
      {!donnees && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
      {donnees && (
        <>
          <div className="adm-form">
            <label className="adm-field"><span>{t('catalog.pricing.pdfPrice')}</span><input className="adm-input" inputMode="decimal" value={pdf} onChange={(e) => { setPdf(e.target.value); setMessage(''); }} /></label>
            <label className="adm-field"><span>{t('catalog.pricing.packDiscount')}</span><input className="adm-input" inputMode="numeric" value={remise} onChange={(e) => { setRemise(e.target.value); setMessage(''); }} /><small>{t('catalog.pricing.packHelp')}</small></label>
          </div>
          <div className="adm-actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={enregistrer} disabled={!modifie || enCours}>{enCours ? t('catalog.common.saving') : t('catalog.common.save')}</button>
          </div>
        </>
      )}
      {message && <p className="adm-note adm-flash" role="status">{message}</p>}
      {erreur && <p className="adm-error" role="alert">{erreur}</p>}
    </section>
  );
}

export default function PricingSection() {
  const { t, i18n } = useTranslation('admin');
  const [produits, setProduits] = useState(null);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    try { setProduits((await listPrintProducts()).products); } catch (err) { setErreur(err?.message || t('catalog.products.loadFailed')); }
  }, [t]);
  useEffect(() => { charger(); }, [charger]);

  const lignesPort = [];
  (produits || []).forEach((p) => {
    Object.entries(p.shipping || {}).forEach(([pays, l]) => lignesPort.push({ p, pays, l }));
  });
  const constats = (produits || []).filter((p) => p.extra && p.extra.lastQuoteCheck);

  return (
    <>
      <PrixGeneraux />

      <section className="adm-card">
        <h2>{t('catalog.pricing.shippingTitle')}</h2>
        <p className="adm-note">{t('catalog.pricing.shippingNote')}</p>
        {erreur && <p className="adm-error" role="alert">{erreur}</p>}
        {!produits && !erreur && <p className="adm-state" role="status">{t('catalog.common.loading')}</p>}
        {produits && (
          <div className="adm-tbl">
            <table>
              <thead>
                <tr>
                  <th>{t('catalog.pricing.cols.country')}</th><th>{t('catalog.pricing.cols.format')}</th><th>{t('catalog.pricing.cols.provider')}</th>
                  <th className="num">{t('catalog.pricing.cols.price')}</th><th className="num">{t('catalog.pricing.cols.cost')}</th><th>{t('catalog.pricing.cols.free')}</th><th />
                </tr>
              </thead>
              <tbody>
                {lignesPort.map(({ p, pays, l }) => (
                  <tr key={`${p.formatId}-${pays}`}>
                    <td><b>{pays}</b></td>
                    <td>{p.name.fr || p.formatId}</td>
                    <td>{p.providerId}</td>
                    <td className="num">{formatEuros(l.priceCents)}</td>
                    <td className="num">{Number.isInteger(l.costCents) ? formatEuros(l.costCents) : '—'}</td>
                    <td>{p.freeShippingThresholdCents ? t('catalog.pricing.freeFrom', { amount: formatEuros(p.freeShippingThresholdCents) }) : '—'}</td>
                    <td><Link className="btn btn-outline btn-sm" to={`${adminPath('products')}?produit=${encodeURIComponent(p.formatId)}`}>{t('catalog.pricing.edit')}</Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="adm-note">{t('catalog.pricing.deliveryNote')}</p>
      </section>

      <div className="adm-cols-eq adm-cols-one">
        <section className="adm-card">
          <h2>{t('catalog.pricing.gapsTitle')}</h2>
          {constats.length === 0 ? (
            <p className="adm-note">{t('catalog.pricing.gapsNone')}</p>
          ) : (
            <ul className="adm-plain">
              {constats.map((p) => {
                const c = p.extra.lastQuoteCheck;
                const dp = c.productCostCents && c.productCostCents.diff;
                const ds = c.shippingCostCents && c.shippingCostCents.diff;
                const conforme = !dp && !ds;
                return (
                  <li key={p.formatId}>
                    <b>{p.name.fr || p.formatId}</b> <Tag tone={conforme ? 'ok' : 'warn'}>{conforme ? t('catalog.drawer.check.same') : t('catalog.drawer.check.gap')}</Tag>
                    <small>
                      {new Date(c.at).toLocaleDateString(i18n.language)} · {c.country} · {t('catalog.pricing.gapDetail', { product: formatEuros(dp || 0), shipping: formatEuros(ds || 0) })}
                    </small>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <PromoSection />

      <section className="adm-card">
        <h2>{t('catalog.pricing.historyTitle')}</h2>
        <ChangeHistory limit={30} />
      </section>
    </>
  );
}
